import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import FileDropzone from '@components/FileDropzone/FileDropzone';
import InputLabel from '@components/InputLabel';

import { Attachments } from '@layouts/DrilldownStyle';

import type { Attachment } from 'generated/types';

const messages = defineMessages({
  attachments: {
    id: 'attachments.attachments',
    defaultMessage: 'Attachments',
  },
});

interface RecordAttachmentFormProps {
  isCreating?: boolean;
  patientId?: string | null;
  onFileChange: (files: DroppedFile[]) => void;
  record: {
    attachments?: Attachment[];
    resourceId?: string;
  };
}

// convert PDF URL to DroppedFile format
const createDroppedFileFromUrl = async (
  url: string,
  title: string = 'Record.pdf'
): Promise<DroppedFile> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch attachment');
  const blob = await response.blob();
  const file = new File([blob], title, { type: 'application/pdf' });
  const droppedFile: DroppedFile = Object.assign(file, {
    preview: url,
    path: title,
    baseName: title.replace('.pdf', ''),
    uploaded: false,
  });
  return droppedFile;
};

export default function RecordAttachmentForm({
  isCreating,
  patientId,
  record,
  onFileChange: handleFileChange,
}: RecordAttachmentFormProps) {
  const { formatMessage } = useIntl();
  const [initialFiles, setInitialFiles] = React.useState<
    DroppedFile[] | undefined
  >(undefined);
  const hasLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (hasLoadedRef.current) return;

    const loadInitialFiles = async () => {
      if (!isCreating || !record.attachments) {
        setInitialFiles([]);
        hasLoadedRef.current = true;
        return;
      }

      const pdfAttachment = record.attachments.find(
        (att) => att.url && att.contentType === 'application/pdf'
      );

      if (pdfAttachment?.url) {
        try {
          const droppedFile = await createDroppedFileFromUrl(
            pdfAttachment.url,
            pdfAttachment.title || 'Record.pdf'
          );

          setInitialFiles([droppedFile]);
          handleFileChange([droppedFile]);
        } catch {
          setInitialFiles([]);
        }
      } else {
        setInitialFiles([]);
      }
      hasLoadedRef.current = true;
    };

    loadInitialFiles();
  }, [isCreating, record.attachments, handleFileChange]);

  return (
    <>
      {isCreating ? (
        <>
          <InputLabel
            label={formatMessage(messages.attachments)}
            style={{ color: 'primary.main' }}
          />
          {initialFiles !== undefined && (
            <FileDropzone
              onFileChange={handleFileChange}
              initialFiles={initialFiles}
            />
          )}
        </>
      ) : (
        <Attachments
          patient_id={patientId}
          attachments={record.attachments}
          resource={record.resourceId}
        />
      )}
    </>
  );
}
